// useBaseCreateModals.js

import { useCallback, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

/**
 * useBaseCreateModals
 *
 * Owns open state and open handlers for the sidebar create modals
 * (database, channel, message, dashboard) so both bucket "+ New" buttons
 * and the top-level AddModal can launch the same flows.
 *
 * @returns {{ modalState: object, openers: object }}
 */
export default function useBaseCreateModals() {
  const { user } = useUser();
  const { activeBase } = useBase();

  const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [conversationModalOpen, setConversationModalOpen] = useState(false);
  const [addDatabaseModalOpen, setAddDatabaseModalOpen] = useState(false);

  const canCreate = Boolean(user?.key && activeBase?.key);

  const openDashboard = useCallback(() => {
    if (!canCreate) return;
    setDashboardModalOpen(true);
  }, [canCreate]);

  const openChannel = useCallback(() => {
    if (!canCreate) return;
    setChannelModalOpen(true);
  }, [canCreate]);

  const openMessage = useCallback(() => {
    if (!canCreate) return;
    setConversationModalOpen(true);
  }, [canCreate]);

  const openDatabase = useCallback(() => {
    if (!canCreate) return;
    setAddDatabaseModalOpen(true);
  }, [canCreate]);

  return {
    modalState: {
      dashboardModalOpen,
      setDashboardModalOpen,
      channelModalOpen,
      setChannelModalOpen,
      conversationModalOpen,
      setConversationModalOpen,
      addDatabaseModalOpen,
      setAddDatabaseModalOpen,
      activeBaseKey: activeBase?.key || null,
    },
    openers: {
      openDashboard,
      openChannel,
      openMessage,
      openDatabase,
    },
  };
}
